/**
 * Phase 1 placeholder datasets. No real math — all values illustrative.
 * Replace with engine outputs / live data feeds in Phase 2.
 */

export const SALARY_OPTIONS = [
    { value: 75000, label: "₹75,000" },
    { value: 100000, label: "₹1,00,000" },
    { value: 150000, label: "₹1,50,000" },
    { value: 200000, label: "₹2,00,000" },
    { value: 300000, label: "₹3,00,000" },
    { value: 500000, label: "₹5,00,000" },
];

export const BANK_FOIR_BANDS = [
    { name: "SBI", rule: 55, rate: 8.50 },
    { name: "HDFC", rule: 50, rate: 8.65 },
    { name: "Axis", rule: 60, rate: 8.75 },
    { name: "Kotak", rule: 60, rate: 8.80 },
    { name: "Godrej Finance", rule: 65, rate: 9.10 },
    { name: "Bajaj Finance", rule: 65, rate: 9.25 },
];
export const BANK_DISCLAIMER = "Indicative rates only. Actual sanction depends on borrower profile.";

// Affordability cost breakdown — Bengaluru apartment defaults.
// GST, brokerage, legal fees and maintenance corpus removed (Feb 2026 product decision).
export const COST_BREAKDOWN = [
    { label: "Stamp Duty (5.00%) + Registration (1.00%) + Cess/Surcharge (1.65%)", pct: 7.65 },
    { label: "Interiors Reserve", pct: 8.0 },
];

export const FOIR_COMPARISON = [
    { rule: "50% FOIR", emi: 62800, home: 8400000, status: "conservative" },
    { rule: "55% FOIR", emi: 73800, home: 9900000, status: "balanced" },
    { rule: "60% FOIR", emi: 84600, home: 11400000, status: "stretched" },
    { rule: "65% FOIR", emi: 95200, home: 12900000, status: "risky" },
];

export const REPAYMENT_SUMMARY = {
    principal: 7920000,
    interest: 8770000,
    total: 16690000,
    yearsToHalf: 13,
    interestSavedPrepay: 1820000,
};

// ─── Market Intelligence dataset ───────────────────────────────────
// Single source of truth: /app/frontend/src/homeiq/data/markets.json
// To update market data, edit that file. No code changes required —
// this module reads the file at build time and derives:
//   • MARKETS               (transformed via buildMarket)
//   • MARKET_DATA_SOURCE    (title + auto-counted row length)
//   • MARKET_DATA_LAST_UPDATED (metadata.last_updated)
import marketDataset from "@/homeiq/data/markets.json";

export const MARKET_DATA_SOURCE = `${marketDataset.metadata.title} (${marketDataset.markets.length} markets)`;
export const MARKET_DATA_LAST_UPDATED = marketDataset.metadata.last_updated;
export const MARKET_DATA_NOTES = marketDataset.metadata.notes;

// Segment is derived from the Mid PSF band so the existing segment filter
// keeps working without rewriting the UI. Thresholds calibrated against the
// verified master dataset so all 4 tiers carry a meaningful population.
function segmentFromMidPsf(midPsf) {
    if (midPsf < 10000) return "Affordable";
    if (midPsf < 13000) return "Mid";
    if (midPsf < 18000) return "Premium";
    return "Luxury";
}

function buildMarket(row) {
    // Back-compat shims: legacy consumers read `m.psf` and `m.context.risk`.
    const psf = row.psfBands.mid;
    const segment = segmentFromMidPsf(row.psfBands.mid);
    const context = {
        ...(row.context || {}),
        risk: row.investmentRisk, // mirror structured field into legacy slot
    };
    return { ...row, city: "Bengaluru", psf, segment, context };
}

export const MARKETS = marketDataset.markets.map(buildMarket);

export const REGIONS = [
    { id: "south", name: "South India", states: ["KA", "TN", "TG", "AP"], avgGrowth: 7.4, totalMarkets: 11, wealth10y: 9800000 },
    { id: "west", name: "West India", states: ["MH", "GJ"], avgGrowth: 5.8, totalMarkets: 7, wealth10y: 11200000 },
    { id: "north", name: "North India (NCR)", states: ["DL", "HR", "UP"], avgGrowth: 6.4, totalMarkets: 4, wealth10y: 8600000 },
    { id: "east", name: "East India", states: ["WB", "OD"], avgGrowth: 4.9, totalMarkets: 2, wealth10y: 5400000 },
    { id: "central", name: "Central India", states: ["MP", "CG"], avgGrowth: 5.2, totalMarkets: 2, wealth10y: 4900000 },
];

export const WEALTH_TRAJECTORY = {
    5: 3200000,
    10: 9800000,
    15: 19400000,
    20: 33600000,
};

// Spec-aligned 8 categories with weighted maxes (sum of maxes = 100).
// Phase 1 placeholder scores sum to 78 — same as VASTU_OVERALL.score.
export const VASTU_DIMENSIONS = [
    { id: "entrance", label: "Entrance", maxScore: 20, score: 18, question: "Which direction does the front door face?" },
    { id: "kitchen", label: "Kitchen", maxScore: 20, score: 14, question: "Which zone holds the cooking flame?" },
    { id: "bedroom", label: "Master Bedroom", maxScore: 15, score: 12, question: "Where does the master bedroom sit?" },
    { id: "toilets", label: "Toilets", maxScore: 15, score: 9, question: "Are toilets clear of sensitive zones?" },
    { id: "ne", label: "North-East Zone", maxScore: 10, score: 8, question: "Is the north-east open and light?" },
    { id: "sw", label: "South-West Zone", maxScore: 10, score: 7, question: "Is the south-west grounded with weight?" },
    { id: "balcony", label: "Balcony", maxScore: 5, score: 5, question: "Which way does the balcony face?" },
    { id: "brahmasthan", label: "Brahmasthan", maxScore: 5, score: 5, question: "Is the centre of the home open?" },
];

export const VASTU_OVERALL = {
    score: 78,
    confidence: "Medium", // High | Medium | Low — placeholder
    summary: "The home reads well on entrance, balcony and the south-west anchor. A toilet in the north-east and a partially occupied Brahmasthan are the issues to address.",
};

// Threshold-based verdict (per spec)
export const VASTU_VERDICTS = {
    strong: { label: "Strong Traditional Alignment", min: 75, interpretation: "This home meets most traditional Vastu principles. HomeIQ suggests proceeding with confidence — minor refinements aside." },
    moderate: { label: "Moderate Alignment", min: 55, interpretation: "This home is workable but carries known compromises. HomeIQ suggests planning small remediations after moving in." },
    concerns: { label: "Traditional Concerns Identified", min: 0, interpretation: "Significant traditional Vastu concerns are present. HomeIQ suggests consulting a practitioner before committing." },
};

export const VASTU_RED_FLAGS = [
    { id: "rf1", title: "Toilet in North-East zone", body: "Deducts up to 7 points from Toilet Placement and impacts the NE Zone score. Consider relocating or ritual remediation." },
    { id: "rf2", title: "Brahmasthan partially occupied", body: "The centre carries heavy furniture, restricting open flow. Clearing the central axis is a high-impact change." },
];

export const VASTU_POSITIVES = [
    { id: "pos1", title: "Entrance faces North", body: "Within the most favourable arc for daily energy and prosperity." },
    { id: "pos2", title: "Master bedroom anchored South-West", body: "Strong, stable placement aligned with the home's grounding zone." },
    { id: "pos3", title: "Balcony faces East", body: "Favourable for morning light and air circulation." },
];

export const VASTU_CONCERNS = [
    { id: "con1", title: "Kitchen in the West", body: "Workable but not ideal — orient the stove so the cook faces East where possible." },
    { id: "con2", title: "South-West weight is moderate", body: "Reinforce with heavier storage or wardrobe block to strengthen the home's anchor." },
];

export const VASTU_METHODOLOGY = [
    { step: 1, label: "Orientation", body: "We identify the compass direction and the apartment's true facing using the uploaded plan." },
    { step: 2, label: "Room Detection", body: "Every room is detected and mapped to its directional zone with a confidence value." },
    { step: 3, label: "Zone Mapping", body: "We compute the status of the three special zones — North-East, South-West and Brahmasthan." },
    { step: 4, label: "Scoring", body: "We score the 8 categories deterministically using fixed rule tables — the same plan always yields the same score." },
    { step: 5, label: "Recommendations", body: "We surface red flags, positives and concerns — and where helpful, what to adjust." },
];

export const EMI_SCHEDULE = Array.from({ length: 20 }, (_, i) => {
    const year = i + 1;
    const principal = Math.round(380000 + i * 32000);
    const interest = Math.round(720000 - i * 32000);
    return { year, principal, interest, total: principal + interest, balance: Math.max(0, 7920000 - (i + 1) * 380000) };
});
